import { Link } from "react-router-dom";
import { imgUrl, isBoosted } from "@/lib/api";
import { formatRate } from "@/lib/services";
import { RatingBadge } from "@/components/Stars";
import AvailableBadge from "@/components/AvailableBadge";
import VerifiedBadge from "@/components/VerifiedBadge";
import { Heart, MapPin, Sparkles, Clock, Phone } from "lucide-react";

export default function ServiceCard({ product, favorited, onToggleFav }) {
  const img = product.images?.length ? imgUrl(product.images[0]) : null;
  const featured = isBoosted(product);
  const to = `/dienst/${product.id}`;
  return (
    <div
      data-testid={`service-card-${product.id}`}
      className={`group relative overflow-hidden rounded-xl border transition-colors shadow-lg animate-fade-up ${product.available_today && !featured ? "border-emerald-500/60 bg-zinc-900/70 ring-1 ring-emerald-500/30" : featured ? "border-amber-500/60 bg-zinc-900/70 ring-1 ring-amber-500/30" : "border-zinc-800 bg-zinc-900/60 hover:bg-zinc-900 hover:border-rose-900/60"}`}
    >
      <Link to={to} className="block">
        <div className="relative aspect-[3/4] overflow-hidden bg-zinc-950">
          {img ? (
            <img src={img} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="flex h-full items-center justify-center text-zinc-700 font-serif text-lg">Geen foto</div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent" />
          <div className="absolute top-3 left-3 flex flex-col gap-2 items-start">
            {featured && (
              <span className="flex items-center gap-1 rounded-full bg-amber-500/90 text-amber-950 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1">
                <Sparkles className="h-3 w-3" /> Uitgelicht
              </span>
            )}
            <VerifiedBadge show={product.seller_verified} compact testid={`verified-badge-${product.id}`} />
            <AvailableBadge available={product.available_today} testid={`${product.available_today ? "available" : "unavailable"}-today-badge-${product.id}`} />
            <span className="rounded-full bg-zinc-950/70 backdrop-blur text-rose-300 border border-rose-800/50 text-[10px] font-mono uppercase tracking-wider px-2.5 py-1">
              {product.category}
            </span>
          </div>
          <div className="absolute bottom-3 left-3 right-3">
            <div className="font-serif text-xl text-zinc-50 leading-tight line-clamp-1">{product.title}</div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-zinc-300">
              {product.gender && <span>{product.gender}</span>}
              {product.age && <span>· {product.age} jaar</span>}
              <span className="flex items-center gap-1">· <MapPin className="h-3 w-3" /> {product.location || "Onbekend"}</span>
            </div>
          </div>
        </div>
      </Link>
      {onToggleFav && (
        <button
          data-testid={`favorite-button-${product.id}`}
          onClick={() => onToggleFav(product.id)}
          className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-zinc-950/70 backdrop-blur border border-zinc-700 text-zinc-300 hover:text-rose-400 transition-colors"
        >
          <Heart className={`h-4 w-4 ${favorited ? "fill-rose-500 text-rose-500" : ""}`} />
        </button>
      )}
      <div className="p-3 sm:p-4 flex items-center justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <span data-testid={`service-rate-${product.id}`} className="font-mono text-sm sm:text-base font-semibold text-rose-400 whitespace-nowrap">{formatRate(product)}</span>
          <RatingBadge rating={product.seller_rating} testid={`service-rating-${product.id}`} />
        </div>
        <span className="flex items-center gap-2 text-[11px] text-zinc-500">
          {product.availability && <Clock className="h-3.5 w-3.5" title={product.availability} />}
          {product.show_phone && <Phone className="h-3.5 w-3.5 text-emerald-400" title="Telefoonnummer beschikbaar" />}
          {product.for_whom && <span className="hidden sm:inline uppercase tracking-wide">Voor {product.for_whom.toLowerCase()}</span>}
        </span>
      </div>
    </div>
  );
}
